/** Scorul unei soluții de structură, explicat într-un singur loc.
 *
 * Scorul are patru termeni pe o scară fixă 0–100, mai mic e mai bun: 55 %
 * verificările picate, 10 % costul de construire, 25 % cât de repetitivă e
 * structura — câte tipuri diferite de stâlp, de grindă și câte diametre de
 * armătură are (v. `structure/recommend.PONDERI` și `structure/clase.py`) —
 * și 10 % grila: câte travee stau pe modulul de parcare și câte deschideri
 * diferite are cadrul (`structure/interax.py`).
 *
 * Cifrele vin gata calculate din `summary.scor_parti`; aici se scrie doar CE
 * înseamnă. Într-un singur loc, fiindcă aceeași explicație apare pe cardul
 * soluției, în panoul ei, în lista de soluții a structurii și pe fișă — iar
 * patru copii ale aceluiași text diverg la prima schimbare de ponderi.
 */

import { numar, t, tn } from "./i18n";
import type { Cheie } from "./i18n";

export type UniformitateParte = {
  valoare?: number | null;
  masurat?: boolean;
  clase?: number | null;
  elemente?: number | null;
  nr?: number | null;
  tinta?: number | null;
  gama?: number | null;
};

export type GrilaParte = {
  valoare?: number | null;
  masurat?: boolean;
  modul_m?: number | null;
  nr?: number | null;
  pe_modul?: number | null;
  nr_distincte?: number | null;
  dominanta_m?: number | null;
  dominanta_nr?: number | null;
};

export type ScorParti = {
  total?: number;
  validari?: number;
  cost?: number;
  uniformitate?: number;
  grila?: number;
  /** penalizarea de conformare: stă DEASUPRA scării de 100 */
  conformare?: number;
  nr_high?: number;
  validari_brut?: number;
  validari_plafon?: number;
  high?: number;
  med?: number;
  low?: number;
  cost_eur?: number | null;
  cost_referinta_eur?: number | null;
  cost_masurat?: boolean;
  uniformitate_parti?: Record<string, UniformitateParte>;
  grila_parti?: Record<string, GrilaParte>;
  ponderi?: Record<string, number>;
};

const PONDERI_IMPLICITE: Record<string, number> = {
  validari: 55, cost: 10, uniformitate: 25, grila: 10,
};

function nr(v: number | null | undefined, zecimale = 1): string {
  return numar(v, { minimumFractionDigits: 0, maximumFractionDigits: zecimale });
}

/** Numele fiecărui termen al scorului. Record, nu un `switch`: un termen nou
 *  se adaugă aici și nu poate rămâne fără nume. */
const NUME_TERMEN: Record<"validari" | "cost" | "uniformitate" | "grila", Cheie> = {
  validari: "scor.validari",
  cost: "scor.cost",
  uniformitate: "scor.uniformitate",
  grila: "scor.grila",
};

/** «validări 21,6 din 65 · cost 5,1 din 10 · uniformitate 10,5 din 25»,
 *  plus penalizarea de conformare când o verificare globală încă pică. */
export function desfacereaScorului(parti?: ScorParti | null): string {
  if (!parti) return "";
  const p = parti.ponderi ?? PONDERI_IMPLICITE;
  const bucati = (["validari", "cost", "uniformitate", "grila"] as const)
    .filter((k) => parti[k] != null && p[k] != null)
    .map((k) => t("scor.parte", {
      nume: t(NUME_TERMEN[k]), valoare: nr(parti[k]), total: nr(p[k], 0),
    }));
  if (parti.conformare) {
    const n = parti.nr_high ?? 0;
    bucati.push(t("scor.conformare", {
      valoare: nr(parti.conformare, 0),
      detaliu: tn("scor.globale_picate#n", n),
    }));
  }
  return bucati.join(" · ");
}

/** Ce a NUMĂRAT uniformitatea: clasele de element și diametrele. */
export function desfacereaUniformitatii(parti?: ScorParti | null): string {
  const u = parti?.uniformitate_parti;
  if (!u) return "";
  const bucati: string[] = [];
  // Cheie pe familie, nu un nume interpolat: în altă limbă «tipuri de stâlpi»
  // și «tipuri de grinzi» nu sunt aceeași propoziție cu un cuvânt schimbat.
  for (const [familie, cheie] of [["stalpi", "scor.tipuri_stalpi"],
                                  ["grinzi", "scor.tipuri_grinzi"]] as const) {
    const p = u[familie];
    if (p?.masurat && p.clase != null && p.elemente != null) {
      bucati.push(t(cheie, { clase: nr(p.clase, 0),
                             elemente: nr(p.elemente, 0) }));
    }
  }
  const d = u.diametre;
  if (d?.masurat && d.nr != null) {
    bucati.push(t("scor.diametre", { nr: nr(d.nr, 0) })
                + (d.tinta != null
                   ? t("scor.diametre_tinta", { tinta: nr(d.tinta, 0) }) : ""));
  }
  return bucati.join(" · ");
}

/** Ce a NUMĂRAT grila: traveele pe modulul de parcare și deschiderile
 *  distincte. */
export function desfacereaGrilei(parti?: ScorParti | null): string {
  const g = parti?.grila_parti;
  if (!g) return "";
  const bucati: string[] = [];
  const m = g.modul;
  if (m?.masurat && m.nr != null && m.pe_modul != null) {
    bucati.push(t("scor.travee_modul", {
      peModul: nr(m.pe_modul, 0), nr: nr(m.nr, 0),
      modul: nr(m.modul_m ?? 2.5, 2),
    }));
  }
  const e = g.egalitate;
  if (e?.masurat && e.nr_distincte != null) {
    bucati.push(t("scor.deschideri", { nr: nr(e.nr_distincte, 0) })
                + (e.dominanta_m != null && e.dominanta_nr != null
                   ? t("scor.deschideri_dominanta", {
                       metri: nr(e.dominanta_m, 2),
                       travee: nr(e.dominanta_nr, 0),
                     }) : ""));
  }
  return bucati.join(" · ");
}

/** Explicația întreagă, pentru `title`. `scurt` lasă afară rândul de subsol. */
export function explicaScorul(scor?: number | null, parti?: ScorParti | null,
                              scurt = false): string {
  const randuri = [
    scor != null ? t("scor.titlu", { valoare: nr(scor, 2) })
                 : t("scor.titlu_fara_cifra"),
  ];
  const desfacere = desfacereaScorului(parti);
  if (desfacere) randuri.push(desfacere);
  randuri.push(t("scor.ponderi"));
  const numarate = desfacereaUniformitatii(parti);
  if (numarate) randuri.push(numarate);
  const grila = desfacereaGrilei(parti);
  if (grila) randuri.push(grila);
  if (parti && parti.cost_masurat === false) {
    randuri.push(t("scor.cost_nemasurat"));
  }
  if (!scurt) {
    randuri.push(t("scor.curat"));
    randuri.push(t("scor.alege_platforma"));
  }
  return randuri.join("\n");
}
